import { useSafeAreaInsets } from "react-native-safe-area-context";

import { TotalOnboardingSteps } from "@/constants/Onboarding";
import { TextTestID } from "@/constants/TestID";
import { TextSeparator } from "@/constants/Text";
import { OnboardingProgressCard } from "@/ui/components/onboarding/v2/cards/OnboardingProgressCard";
import { Button, FAIconName, Image, Text } from "@/ui/core";
import { HStack, VStack } from "@/ui/layout";
import RadialGradientBackground from "@/ui/layout/RadialGradientBackground";
import {
  getOnboardingIllustrationIcon,
  OnboardingIllustrationName,
} from "@/ui/theme/icons/illustrations/onboarding";
import { ColorToken, LayoutToken } from "@/ui/theme/tokens";
import { joinTestIDs } from "@/utils/string.util";

interface OnboardingOverviewCardProps {
  currentStepIndex: number;
  currentStepLabel: string;
  onContinuePress: () => void;
  patientName?: string;
}

const baseTestID = joinTestIDs("onboarding-overview", TextTestID.Card);

const OnboardingOverviewCard = ({
  currentStepIndex,
  currentStepLabel,
  onContinuePress,
  patientName,
}: OnboardingOverviewCardProps) => {
  const { top } = useSafeAreaInsets();

  return (
    <RadialGradientBackground testID={baseTestID}>
      <VStack
        gap={24}
        paddingBottom={40}
        paddingHorizontal={LayoutToken.ContentPaddingHorizontal}
        paddingTop={top + 16}
      >
        <Image
          size={24}
          testID={joinTestIDs(baseTestID, "logo")}
          uriSource={require("@/assets/images/equip-logo.png")}
        />

        <HStack alignItems="center" gap={12}>
          {getOnboardingIllustrationIcon({
            iconName: OnboardingIllustrationName.Placeholder,
          })}
          <VStack gap={2}>
            <Text
              color={ColorToken.Primary90Variant}
              testID={joinTestIDs(baseTestID, "label")}
              variant="captionMedium"
            >
              {`Up next ${TextSeparator.Dot} ${currentStepLabel}`}
            </Text>
            <Text
              color={ColorToken.NeutralPrimary}
              testID={joinTestIDs(baseTestID, "title")}
              variant="h3"
            >
              Getting set up
            </Text>
          </VStack>
        </HStack>

        <Text
          color={ColorToken.NeutralSecondary}
          testID={joinTestIDs(baseTestID, "description")}
          variant="body"
        >
          These steps give your care team the full picture before you start —
          your history, your goals, and what matters to you. Save anytime and
          pick up where you left off.
        </Text>

        <VStack gap={24}>
          <OnboardingProgressCard
            currentStepIndex={currentStepIndex}
            patientName={patientName}
            testID={baseTestID}
            totalSteps={TotalOnboardingSteps}
          />
          <Button
            isFullWidth
            onPress={onContinuePress}
            rightIcon={FAIconName.ArrowRight}
            testID={joinTestIDs(baseTestID, "continue")}
            title="Continue"
          />
        </VStack>
      </VStack>
    </RadialGradientBackground>
  );
};

export type { OnboardingOverviewCardProps };
export { OnboardingOverviewCard };
